<template>
  <div class="dept-form-page py-3">
    <header class="dept-form-toolbar">
      <button type="button" class="back-btn" @click="goBack" aria-label="Back to departments">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="19" y1="12" x2="5" y2="12"/>
          <polyline points="12 19 5 12 12 5"/>
        </svg>
        <span>Departments</span>
      </button>
      <div class="dept-form-toolbar__title">
        <span class="dept-form-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Create Department</h1>
          <p>Add a department under <strong>{{ activeOrganisationName || 'the active organisation' }}</strong></p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="dept-form-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="dept-form-alert">{{ successMessage }}</CAlert>

    <div class="dept-form-grid">
      <section class="dept-form-card">
        <header class="dept-form-card__head">
          <h2>Department details</h2>
          <p>The name appears in user assignments, call queues, and reports.</p>
        </header>

        <form class="dept-form" @submit.prevent="submit">
          <div class="form-field">
            <label for="dept-name" class="form-label">
              Name
              <span class="form-label__required" aria-hidden="true">*</span>
            </label>
            <input
              id="dept-name"
              v-model.trim="form.name"
              type="text"
              class="form-input"
              placeholder="e.g. Kerala"
              autocomplete="off"
              :disabled="isSubmitting"
              :class="{ 'has-error': fieldErrors.name }"
              @input="clearFieldError('name')"
            />
            <span v-if="fieldErrors.name" class="form-hint form-hint--error">{{ fieldErrors.name }}</span>
            <span v-else-if="duplicateWarning" class="form-hint form-hint--error">{{ duplicateWarning }}</span>
            <span v-else class="form-hint">Must be unique within {{ activeOrganisationName || 'this organisation' }}.</span>
          </div>

          <div class="dept-form__context">
            <span class="dept-form__context-label">Organisation</span>
            <span class="dept-form__context-value">{{ activeOrganisationName || '—' }}</span>
            <span class="dept-form__context-hint">Switch organisations from the sidebar to add a department elsewhere.</span>
          </div>

          <div class="form-actions">
            <button type="button" class="cancel-btn" :disabled="isSubmitting" @click="goBack">Cancel</button>
            <button type="submit" class="submit-btn" :disabled="isSubmitting || !canSubmit">
              <CSpinner v-if="isSubmitting" size="sm" class="submit-btn__spinner" />
              <span>{{ isSubmitting ? 'Creating…' : 'Create Department' }}</span>
            </button>
          </div>
        </form>
      </section>

      <aside class="dept-side">
        <section class="dept-preview-card">
          <header class="dept-preview-card__head">
            <h3>Preview</h3>
            <p>How this department will appear in the OMS.</p>
          </header>
          <div class="dept-preview">
            <span class="dept-preview__marker" :style="previewMarkerStyle" aria-hidden="true"></span>
            <div class="dept-preview__body">
              <span class="dept-preview__name">{{ form.name || 'New department' }}</span>
              <span class="dept-preview__org">{{ activeOrganisationName || '—' }}</span>
            </div>
            <span class="dept-preview__chip">0 users</span>
          </div>
        </section>

        <section class="dept-existing-card">
          <header class="dept-existing-card__head">
            <h3>Existing departments</h3>
            <p>{{ existingHint }}</p>
          </header>
          <div class="dept-existing">
            <div v-if="isLoadingExisting" class="dept-existing__state">
              <CSpinner size="sm" />
              <span>Loading…</span>
            </div>
            <div v-else-if="!existingDepartments.length" class="dept-existing__state dept-existing__state--empty">
              <span>None yet — yours will be the first.</span>
            </div>
            <ul v-else class="dept-existing__list">
              <li
                v-for="dept in existingDepartments"
                :key="dept.id"
                class="dept-existing__item"
                :class="{ 'is-conflict': nameConflicts(dept.name) }"
              >
                <span class="dept-existing__marker" :style="markerStyle(dept.name)" aria-hidden="true"></span>
                <span class="dept-existing__name">{{ dept.name }}</span>
                <span v-if="nameConflicts(dept.name)" class="dept-existing__badge">conflict</span>
              </li>
            </ul>
          </div>
        </section>

        <section class="dept-tips-card">
          <header class="dept-tips-card__head"><h3>Tips</h3></header>
          <ul class="dept-tips">
            <li>Use a short, recognisable name — it shows in user records and reports.</li>
            <li>Two organisations can both have a "Kerala" department — uniqueness is per organisation.</li>
            <li>Departments can't be deleted from the UI; rename them when needs change.</li>
          </ul>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const MARKER_PALETTE = ['#0e5b46', '#1e40af', '#5b21b6', '#92400e', '#115e59', '#7f1d1d']

export default {
  name: 'SettingsCreateDepartment',
  data () {
    return {
      form: { name: '' },
      fieldErrors: {},
      errorMessage: '',
      successMessage: '',
      isSubmitting: false,
      existingDepartments: [],
      isLoadingExisting: false
    }
  },
  computed: {
    activeOrganisationName () {
      return this.$store?.state?.activeOrganisationName || ''
    },
    canSubmit () {
      const name = this.form.name.trim()
      return name.length > 0 && !this.duplicateWarning
    },
    previewMarkerStyle () {
      return { background: this.markerColor(this.form.name || 'New department') }
    },
    existingHint () {
      const count = this.existingDepartments.length
      if (!count) return `In ${this.activeOrganisationName || 'this organisation'}`
      return `${count} in ${this.activeOrganisationName || 'this organisation'}`
    },
    duplicateWarning () {
      const name = this.form.name.trim().toLowerCase()
      if (!name) return ''
      const conflict = this.existingDepartments.some((dept) => String(dept.name || '').toLowerCase() === name)
      return conflict ? `"${this.form.name.trim()}" already exists in this organisation.` : ''
    }
  },
  created () {
    this.loadExisting()
  },
  methods: {
    loadExisting () {
      this.isLoadingExisting = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/department?token=${token}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.existingDepartments = data
          else if (Array.isArray(data?.data)) this.existingDepartments = data.data
          else this.existingDepartments = []
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingExisting = false })
    },
    markerColor (name) {
      const text = String(name || '')
      let hash = 0
      for (let i = 0; i < text.length; i += 1) {
        hash = (hash * 31 + text.charCodeAt(i)) >>> 0
      }
      return MARKER_PALETTE[hash % MARKER_PALETTE.length]
    },
    markerStyle (name) {
      return { background: this.markerColor(name) }
    },
    nameConflicts (deptName) {
      const a = String(deptName || '').trim().toLowerCase()
      const b = this.form.name.trim().toLowerCase()
      return a && b && a === b
    },
    clearFieldError (field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
      this.errorMessage = ''
    },
    validate () {
      const errors = {}
      const name = this.form.name.trim()
      if (!name) {
        errors.name = 'Department name is required.'
      } else if (name.length > 255) {
        errors.name = 'Name must be 255 characters or fewer.'
      }
      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },
    submit () {
      if (!this.validate()) return
      this.isSubmitting = true
      this.errorMessage = ''
      this.successMessage = ''

      const token = localStorage.getItem('api_token')
      const payload = { name: this.form.name.trim() }

      axios.post(`${this.$apiAdress}/api/department?token=${token}`, payload)
        .then((response) => {
          const validationErrors = response.data?.error
          if (validationErrors && typeof validationErrors === 'object') {
            this.fieldErrors = this.flattenValidationErrors(validationErrors)
            this.errorMessage = 'Please fix the errors below.'
            return
          }
          this.successMessage = response.data?.message || 'Department created successfully.'
          setTimeout(() => { this.$router.push('/settings/departments') }, 600)
        })
        .catch((error) => {
          console.log(error)
          const data = error?.response?.data
          if (data?.error && typeof data.error === 'object') {
            this.fieldErrors = this.flattenValidationErrors(data.error)
          }
          this.errorMessage = data?.message || 'Unable to create department. You may not have permission.'
        })
        .finally(() => { this.isSubmitting = false })
    },
    flattenValidationErrors (errors) {
      const flat = {}
      Object.keys(errors).forEach((key) => {
        const value = errors[key]
        flat[key] = Array.isArray(value) ? value[0] : String(value)
      })
      return flat
    },
    goBack () {
      this.$router.push('/settings/departments')
    }
  }
}
</script>

<style scoped>
.dept-form-page {
  padding: 0.25rem 0;
}

.dept-form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}

.dept-form-toolbar {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.25rem 0.25rem 1rem;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.55rem;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.back-btn:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.dept-form-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dept-form-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.dept-form-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.dept-form-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.dept-form-toolbar__title strong {
  color: #0e5b46;
}

.dept-form-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.dept-form-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.dept-form-card__head {
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.dept-form-card__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.dept-form-card__head p {
  margin: 0.2rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.dept-form {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.form-label__required {
  color: #b45309;
  margin-left: 0.15rem;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.85rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.form-input.has-error {
  border-color: #dc2626;
}

.form-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
}

.form-hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.form-hint--error {
  color: #b91c1c;
  font-weight: 600;
}

.dept-form__context {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  background: #f5fbf8;
  border: 1px solid #c7ebda;
}

.dept-form__context-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0e5b46;
  line-height: 1.2;
}

.dept-form__context-value {
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.dept-form__context-hint {
  font-size: 0.72rem;
  color: #4b5563;
  margin-top: 0.2rem;
  line-height: 1.4;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  padding-top: 0.5rem;
  border-top: 1px solid #eef1f7;
  margin-top: 0.5rem;
}

.cancel-btn {
  height: 38px;
  padding: 0 1.1rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cancel-btn:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 38px;
  padding: 0 1.25rem;
  border-radius: 8px;
  border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn__spinner {
  width: 14px;
  height: 14px;
}

/* ── Side panel ── */
.dept-side {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1rem;
}

.dept-preview-card,
.dept-existing-card,
.dept-tips-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.dept-preview-card__head,
.dept-existing-card__head,
.dept-tips-card__head {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.dept-preview-card__head h3,
.dept-existing-card__head h3,
.dept-tips-card__head h3 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.dept-preview-card__head p,
.dept-existing-card__head p {
  margin: 0.15rem 0 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.dept-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
}

.dept-preview__marker {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 4px rgba(255, 255, 255, 0.55);
}

.dept-preview__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.dept-preview__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dept-preview__org {
  font-size: 0.74rem;
  color: #6b7280;
  font-weight: 500;
}

.dept-preview__chip {
  font-size: 0.7rem;
  font-weight: 700;
  color: #4b5563;
  background: #f1f5f9;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
}

.dept-existing {
  padding: 0.55rem;
  max-height: 280px;
  overflow-y: auto;
}

.dept-existing__state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 0.5rem;
  font-size: 0.78rem;
  color: #4b5563;
  font-weight: 500;
}

.dept-existing__state--empty {
  font-style: italic;
  color: #6b7280;
}

.dept-existing__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.dept-existing__item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.55rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #1f2a44;
  transition: background 0.15s ease, color 0.15s ease;
}

.dept-existing__item:hover {
  background: #f8fafc;
}

.dept-existing__item.is-conflict {
  background: #fef2f2;
  color: #991b1b;
}

.dept-existing__marker {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dept-existing__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dept-existing__badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: #fee2e2;
  color: #991b1b;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
}

.dept-tips {
  list-style: none;
  margin: 0;
  padding: 0.55rem 1rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.dept-tips li {
  position: relative;
  padding-left: 1.05rem;
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.45;
}

.dept-tips li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #16a07a;
}

@media (max-width: 991px) {
  .dept-form-grid {
    grid-template-columns: 1fr;
  }
  .dept-side {
    position: static;
  }
  .dept-existing {
    max-height: 320px;
  }
}

@media (max-width: 575px) {
  .dept-form-page {
    padding: 0 0 0.5rem;
  }
  .dept-form-toolbar {
    flex-wrap: wrap;
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .dept-form-toolbar__accent {
    height: 30px;
  }
  .dept-form-toolbar__title h1 {
    font-size: 1rem;
  }
  .dept-form-toolbar__title p {
    font-size: 0.72rem;
  }
  .dept-form-card__head {
    padding: 0.75rem 0.85rem;
  }
  .dept-form {
    padding: 0.85rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
  .cancel-btn,
  .submit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
